import { ReactNode } from "react";
import { AlertTriangle, Info, CheckCircle, XCircle } from "lucide-react";
import { Button } from "@/components/tailwind/button";
import { Text } from "@/components/tailwind/text";

export type BannerVariant = "info" | "warning" | "success" | "error";

interface DashboardBannerProps {
    variant?: BannerVariant;
    title: string;
    description?: string;
    children?: ReactNode;
    action?: {
        label: string;
        onClick: () => void;
    };
    onDismiss?: () => void;
}

const variantStyles: Record<BannerVariant, {
    background: string;
    border: string;
    icon: {
        component: typeof Info;
        className: string;
    };
    text: {
        title: string;
        description: string;
    };
}> = {
    info: {
        background: "bg-blue-50 dark:bg-blue-950",
        border: "border-l-4 border-blue-400 dark:border-blue-500",
        icon: {
            component: Info,
            className: "text-blue-600 dark:text-blue-400"
        },
        text: {
            title: "text-blue-800 dark:text-blue-200",
            description: "text-blue-700 dark:text-blue-300"
        }
    },
    warning: {
        background: "bg-amber-50 dark:bg-amber-950",
        border: "border-l-4 border-amber-400 dark:border-amber-500",
        icon: {
            component: AlertTriangle,
            className: "text-amber-600 dark:text-amber-400"
        },
        text: {
            title: "text-amber-800 dark:text-amber-200",
            description: "text-amber-700 dark:text-amber-300"
        }
    },
    success: {
        background: "bg-green-50 dark:bg-green-950",
        border: "border-l-4 border-green-400 dark:border-green-500",
        icon: {
            component: CheckCircle,
            className: "text-green-600 dark:text-green-400"
        },
        text: {
            title: "text-green-800 dark:text-green-200",
            description: "text-green-700 dark:text-green-300"
        }
    },
    error: {
        background: "bg-red-50 dark:bg-red-950",
        border: "border-l-4 border-red-400 dark:border-red-500",
        icon: {
            component: XCircle,
            className: "text-red-600 dark:text-red-400"
        },
        text: {
            title: "text-red-800 dark:text-red-200",
            description: "text-red-700 dark:text-red-300"
        }
    }
};

export function DashboardBanner({
    variant = "info",
    title,
    description,
    children,
    action,
    onDismiss
}: DashboardBannerProps) {
    const styles = variantStyles[variant];
    const Icon = styles.icon.component;

    return (
        <div 
            className={`${styles.background} ${styles.border} p-4 rounded-r-md`}
            data-testid={`dashboard-banner-${variant}`}
        >
            <div className="flex">
                <div className="flex-shrink-0">
                    <Icon className={`h-5 w-5 ${styles.icon.className}`} />
                </div>
                <div className="ml-3 flex-1">
                    <h3 className={`text-sm font-medium ${styles.text.title}`}>
                        {title}
                    </h3>
                    {description && (
                        <Text className={`mt-2 text-sm ${styles.text.description}`}>
                            {description}
                        </Text>
                    )}
                    {children && (
                        <div className="mt-2">
                            {children}
                        </div>
                    )}
                    {action && (
                        <div className="mt-4">
                            <Button
                                color={variant === "warning" ? "amber" : variant === "error" ? "red" : "zinc"}
                                onClick={action.onClick}
                            >
                                {action.label}
                            </Button>
                        </div>
                    )}
                </div>
                {onDismiss && (
                    <div className="ml-auto pl-3">
                        <button
                            onClick={onDismiss}
                            className={`inline-flex rounded-md p-1.5 hover:bg-black/5 dark:hover:bg-white/5 focus:outline-none focus:ring-2 focus:ring-offset-2 ${styles.icon.className}`}
                        >
                            <span className="sr-only">Dismiss</span>
                            <XCircle className="h-5 w-5" />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}